// src/services/location.service.ts
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
  onSnapshot,
  setDoc,
} from 'firebase/firestore';
import { db } from '../config/firebase';


export interface LocationCapture {
  lat: number;
  lng: number;
  accuracy: number; // meters
  altitude?: number | null;
  altitudeAccuracy?: number | null;
  heading?: number | null;
  speed?: number | null;
  timestamp: number;
  source: 'gps' | 'network' | 'manual';
}

export interface AccountabilityRecord {
  id?: string;
  userId: string;
  userName?: string;
  addressId: string;
  addressLocation: { lat: number; lng: number };
  capturedLocation: { lat: number; lng: number };
  distanceMeters: number;
  withinThreshold: boolean;
  action: 'disposition' | 'survey_submit' | 'check_in' | 'photo_capture' | 'note_added';
  timestamp: Timestamp;
  accuracy: number;
  workloadId?: string;
  projectId?: string;
  metadata?: Record<string, any>;
}

export interface CanvasserLocationUpdate {
  userId: string;
  userName: string;
  location: { lat: number; lng: number };
  accuracy: number;
  timestamp: Timestamp;
  currentWorkloadId?: string;
  currentAddressId?: string;
  status: 'active' | 'idle' | 'offline';
  lastActivityAt: Timestamp;
}

export interface FlaggedEntry {
  id?: string;
  accountabilityRecordId: string;
  userId: string;
  userName: string;
  flagType: 'location_mismatch' | 'rapid_submission' | 'poor_accuracy' | 'suspicious_pattern';
  severity: 'low' | 'medium' | 'high';
  description: string;
  timestamp: Timestamp;
  reviewed: boolean;
  reviewedBy?: string;
  reviewedAt?: Timestamp;
  resolution?: string;
}

export interface CanvasserPerformanceMetrics {
  userId: string;
  userName: string;
  dateRange: { start: Date; end: Date };
  totalDispositions: number;
  surveysCompleted: number;
  averageTimePerAddress: number;
  locationMismatches: number;
  locationMismatchRate: number;
  rapidSubmissions: number;
  rapidSubmissionRate: number;
  hoursActive: number;
  distinctLocationsVisited: number;
}

export interface LocationWatchOptions {
  enableHighAccuracy?: boolean;
  timeout?: number;
  maximumAge?: number;
  onSuccess?: (location: LocationCapture) => void;
  onError?: (error: GeolocationPositionError) => void;
}

export interface ProximityResult {
  withinThreshold: boolean;
  distanceMeters: number;
  capturedLocation: LocationCapture;
  addressLocation: { lat: number; lng: number };
  threshold: number;
}


// Default threshold for proximity validation (100 meters)
export const DEFAULT_PROXIMITY_THRESHOLD = 100;

// Accuracy threshold - warn if GPS accuracy is worse than this (meters)
export const ACCURACY_WARNING_THRESHOLD = 50;


class LocationService {
  private watchId: number | null = null;
  private lastKnownPosition: LocationCapture | null = null;

  isSupported(): boolean {
    return 'geolocation' in navigator;
  }

  async getCurrentPosition(options?: PositionOptions): Promise<LocationCapture> {
    if (!this.isSupported()) {
      throw new Error('Geolocation is not supported by this browser');
    }

    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const location = this.positionToCapture(position);
          this.lastKnownPosition = location;
          resolve(location);
        },
        (error) => {
          reject(this.handleError(error));
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
          ...options,
        }
      );
    });
  }

  watchPosition(options: LocationWatchOptions = {}): number {
    if (!this.isSupported()) {
      throw new Error('Geolocation is not supported by this browser');
    }

    if (this.watchId !== null) {
      this.clearWatch();
    }

    this.watchId = navigator.geolocation.watchPosition(
      (position) => {
        const location = this.positionToCapture(position);
        this.lastKnownPosition = location;
        options.onSuccess?.(location);
      },
      (error) => {
        options.onError?.(error);
      },
      {
        enableHighAccuracy: options.enableHighAccuracy ?? true,
        timeout: options.timeout ?? 10000,
        maximumAge: options.maximumAge ?? 0,
      }
    );

    return this.watchId;
  }

  clearWatch(): void {
    if (this.watchId !== null) {
      navigator.geolocation.clearWatch(this.watchId);
      this.watchId = null;
    }
  }

  getLastKnownPosition(): LocationCapture | null {
    return this.lastKnownPosition;
  }

  /**
   * Calculate distance between two points using Haversine formula
   * @returns Distance in meters
   */
  calculateDistance(
    point1: { lat: number; lng: number },
    point2: { lat: number; lng: number }
  ): number {
    const R = 6371e3; // Earth's radius in meters
    const lat1Rad = this.toRadians(point1.lat);
    const lat2Rad = this.toRadians(point2.lat);
    const deltaLat = this.toRadians(point2.lat - point1.lat);
    const deltaLng = this.toRadians(point2.lng - point1.lng);

    const a =
      Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
      Math.cos(lat1Rad) *
        Math.cos(lat2Rad) *
        Math.sin(deltaLng / 2) *
        Math.sin(deltaLng / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c;
  }

  /**
   * Validate if captured location is within threshold of target address
   */
  async validateProximity(
    addressLocation: { lat: number; lng: number },
    threshold: number = DEFAULT_PROXIMITY_THRESHOLD
  ): Promise<ProximityResult> {
    const capturedLocation = await this.getCurrentPosition();
    const distanceMeters = this.calculateDistance(
      { lat: capturedLocation.lat, lng: capturedLocation.lng },
      addressLocation
    );

    return {
      withinThreshold: distanceMeters <= threshold,
      distanceMeters: Math.round(distanceMeters),
      capturedLocation,
      addressLocation,
      threshold,
    };
  }

  /**
   * Create an accountability record for tracking
   */
  createAccountabilityRecord(
    userId: string,
    addressId: string,
    addressLocation: { lat: number; lng: number },
    capturedLocation: LocationCapture,
    action: AccountabilityRecord['action'],
    additionalData?: Partial<AccountabilityRecord>
  ): AccountabilityRecord {
    const distanceMeters = this.calculateDistance(
      { lat: capturedLocation.lat, lng: capturedLocation.lng },
      addressLocation
    );

    return {
      userId,
      addressId,
      addressLocation,
      capturedLocation: {
        lat: capturedLocation.lat,
        lng: capturedLocation.lng,
      },
      distanceMeters: Math.round(distanceMeters),
      withinThreshold: distanceMeters <= DEFAULT_PROXIMITY_THRESHOLD,
      action,
      timestamp: Timestamp.now(),
      accuracy: capturedLocation.accuracy,
      ...additionalData,
    };
  }

  isAccuracyAcceptable(accuracy: number): boolean {
    return accuracy <= ACCURACY_WARNING_THRESHOLD;
  }

  getAccuracyQuality(accuracy: number): 'excellent' | 'good' | 'fair' | 'poor' {
    if (accuracy <= 10) return 'excellent';
    if (accuracy <= 25) return 'good';
    if (accuracy <= 50) return 'fair';
    return 'poor';
  }

  formatDistance(meters: number): string {
    if (meters < 1000) {
      return `${Math.round(meters)} m`;
    }
    return `${(meters / 1000).toFixed(1)} km`;
  }

  private positionToCapture(position: GeolocationPosition): LocationCapture {
    return {
      lat: position.coords.latitude,
      lng: position.coords.longitude,
      accuracy: position.coords.accuracy,
      altitude: position.coords.altitude,
      altitudeAccuracy: position.coords.altitudeAccuracy,
      heading: position.coords.heading,
      speed: position.coords.speed,
      timestamp: position.timestamp,
      source: position.coords.accuracy <= 20 ? 'gps' : 'network',
    };
  }

  private toRadians(degrees: number): number {
    return (degrees * Math.PI) / 180;
  }

  private handleError(error: GeolocationPositionError): Error {
    switch (error.code) {
      case error.PERMISSION_DENIED:
        return new Error(
          'Location permission denied. Please enable location access in your browser settings.'
        );
      case error.POSITION_UNAVAILABLE:
        return new Error(
          'Location information is unavailable. Please check your device GPS settings.'
        );
      case error.TIMEOUT:
        return new Error(
          'Location request timed out. Please try again or move to a location with better signal.'
        );
      default:
        return new Error(`Unknown location error: ${error.message}`);
    }
  }

  async saveAccountabilityRecord(record: AccountabilityRecord): Promise<string> {
    const docRef = await addDoc(collection(db, 'accountabilityRecords'), {
      ...record,
      timestamp: record.timestamp || Timestamp.now(),
    });

    // Auto-flag if location mismatch
    if (!record.withinThreshold) {
      await this.createFlag({
        accountabilityRecordId: docRef.id,
        userId: record.userId,
        userName: record.userName || 'Unknown',
        flagType: 'location_mismatch',
        severity: record.distanceMeters > 500 ? 'high' : record.distanceMeters > 200 ? 'medium' : 'low',
        description: `Location mismatch: ${record.distanceMeters}m from address during ${record.action}`,
        timestamp: Timestamp.now(),
        reviewed: false,
      });
    }

    return docRef.id;
  }

  async updateCanvasserLocation(update: CanvasserLocationUpdate): Promise<void> {
    const docRef = doc(db, 'canvasserLocations', update.userId);
    await setDoc(docRef, {
      ...update,
      timestamp: Timestamp.now(),
      lastActivityAt: Timestamp.now(),
    }, { merge: true });
  }

  async markCanvasserOffline(userId: string): Promise<void> {
    await updateDoc(doc(db, 'canvasserLocations', userId), {
      status: 'offline',
    });
  }

  subscribeToCanvasserLocations(
    callback: (locations: CanvasserLocationUpdate[]) => void
  ): () => void {
    const q = query(
      collection(db, 'canvasserLocations'),
      where('status', 'in', ['active', 'idle'])
    );

    return onSnapshot(q, (snapshot) => {
      const locations = snapshot.docs.map((d) => ({
        ...d.data(),
        userId: d.id,
      })) as CanvasserLocationUpdate[];
      callback(locations);
    });
  }

  async getUserAccountabilityRecords(
    userId: string,
    startDate: Date,
    endDate: Date
  ): Promise<AccountabilityRecord[]> {
    const q = query(
      collection(db, 'accountabilityRecords'),
      where('userId', '==', userId),
      where('timestamp', '>=', Timestamp.fromDate(startDate)),
      where('timestamp', '<=', Timestamp.fromDate(endDate)),
      orderBy('timestamp', 'desc')
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map((d) => ({
      id: d.id,
      ...d.data(),
    })) as AccountabilityRecord[];
  }

  async getWorkloadAccountabilityRecords(workloadId: string): Promise<AccountabilityRecord[]> {
    const q = query(
      collection(db, 'accountabilityRecords'),
      where('workloadId', '==', workloadId),
      orderBy('timestamp', 'desc')
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map((d) => ({
      id: d.id,
      ...d.data(),
    })) as AccountabilityRecord[];
  }

  async createFlag(flag: FlaggedEntry): Promise<string> {
    const docRef = await addDoc(collection(db, 'flaggedEntries'), flag);
    return docRef.id;
  }

  async getFlaggedEntries(filters?: {
    userId?: string;
    reviewed?: boolean;
    severity?: 'low' | 'medium' | 'high';
  }): Promise<FlaggedEntry[]> {
    let q = query(
      collection(db, 'flaggedEntries'),
      orderBy('timestamp', 'desc'),
      limit(100)
    );

    const snapshot = await getDocs(q);
    let results = snapshot.docs.map((d) => ({
      id: d.id,
      ...d.data(),
    })) as FlaggedEntry[];

    // Filter in memory for complex conditions
    if (filters?.userId) {
      results = results.filter((f) => f.userId === filters.userId);
    }
    if (filters?.reviewed !== undefined) {
      results = results.filter((f) => f.reviewed === filters.reviewed);
    }
    if (filters?.severity) {
      results = results.filter((f) => f.severity === filters.severity);
    }

    return results;
  }

  async reviewFlag(flagId: string, reviewedBy: string, resolution: string): Promise<void> {
    await updateDoc(doc(db, 'flaggedEntries', flagId), {
      reviewed: true,
      reviewedBy,
      reviewedAt: Timestamp.now(),
      resolution,
    });
  }

  async checkForRapidSubmissions(
    userId: string,
    userName: string,
    currentTimestamp: number,
    thresholdSeconds: number = 30
  ): Promise<boolean> {
    const q = query(
      collection(db, 'accountabilityRecords'),
      where('userId', '==', userId),
      orderBy('timestamp', 'desc'),
      limit(1)
    );

    const snapshot = await getDocs(q);
    if (snapshot.empty) return false;

    const lastRecord = snapshot.docs[0].data();
    const lastTimestamp = lastRecord.timestamp.toMillis();
    const timeDiff = (currentTimestamp - lastTimestamp) / 1000;

    if (timeDiff < thresholdSeconds) {
      await this.createFlag({
        accountabilityRecordId: snapshot.docs[0].id,
        userId,
        userName,
        flagType: 'rapid_submission',
        severity: timeDiff < 10 ? 'high' : 'medium',
        description: `Rapid submission: ${Math.round(timeDiff)} seconds since last action`,
        timestamp: Timestamp.now(),
        reviewed: false,
      });
      return true;
    }

    return false;
  }

  async getCanvasserPerformanceMetrics(
    userId: string,
    userName: string,
    startDate: Date,
    endDate: Date
  ): Promise<CanvasserPerformanceMetrics> {
    const records = await this.getUserAccountabilityRecords(userId, startDate, endDate);

    const dispositions = records.filter((r) => r.action === 'disposition');
    const surveys = records.filter((r) => r.action === 'survey_submit');
    const locationMismatches = records.filter((r) => !r.withinThreshold);

    // Calculate rapid submissions
    const sortedRecords = [...records].sort(
      (a, b) => a.timestamp.toMillis() - b.timestamp.toMillis()
    );
    let rapidSubmissions = 0;
    for (let i = 1; i < sortedRecords.length; i++) {
      const timeDiff = sortedRecords[i].timestamp.toMillis() - sortedRecords[i - 1].timestamp.toMillis();
      if (timeDiff < 30000) rapidSubmissions++;
    }

    // Calculate average time per address
    let totalTime = 0;
    for (let i = 1; i < sortedRecords.length; i++) {
      totalTime += sortedRecords[i].timestamp.toMillis() - sortedRecords[i - 1].timestamp.toMillis();
    }
    const avgTimePerAddress = sortedRecords.length > 1
      ? Math.round(totalTime / (sortedRecords.length - 1) / 1000)
      : 0;

    // Calculate hours active
    const timestamps = records.map((r) => r.timestamp.toMillis());
    const hoursActive = timestamps.length > 0
      ? (Math.max(...timestamps) - Math.min(...timestamps)) / (1000 * 60 * 60)
      : 0;

    // Count distinct locations
    const locationSet = new Set<string>();
    records.forEach((r) => {
      locationSet.add(`${r.capturedLocation.lat.toFixed(5)},${r.capturedLocation.lng.toFixed(5)}`);
    });

    return {
      userId,
      userName,
      dateRange: { start: startDate, end: endDate },
      totalDispositions: dispositions.length,
      surveysCompleted: surveys.length,
      averageTimePerAddress: avgTimePerAddress,
      locationMismatches: locationMismatches.length,
      locationMismatchRate: dispositions.length > 0
        ? Math.round((locationMismatches.length / dispositions.length) * 100)
        : 0,
      rapidSubmissions,
      rapidSubmissionRate: dispositions.length > 0
        ? Math.round((rapidSubmissions / dispositions.length) * 100)
        : 0,
      hoursActive: Math.round(hoursActive * 10) / 10,
      distinctLocationsVisited: locationSet.size,
    };
  }

  /**
   * Get historical canvasser paths for a given date.
   * Returns accountability records grouped by userId for polyline drawing.
   */
  async getHistoricalCanvasserPaths(
    projectId: string,
    date: Date
  ): Promise<Map<string, { userName: string; records: AccountabilityRecord[] }>> {
    const startOfDay = new Date(date);
    startOfDay.setHours(0, 0, 0, 0);
    const endOfDay = new Date(date);
    endOfDay.setHours(23, 59, 59, 999);

    const q = query(
      collection(db, 'accountabilityRecords'),
      where('projectId', '==', projectId),
      where('timestamp', '>=', Timestamp.fromDate(startOfDay)),
      where('timestamp', '<=', Timestamp.fromDate(endOfDay)),
      orderBy('timestamp', 'asc')
    );

    const snapshot = await getDocs(q);
    const grouped = new Map<string, { userName: string; records: AccountabilityRecord[] }>();

    snapshot.docs.forEach((d) => {
      const record = { id: d.id, ...d.data() } as AccountabilityRecord;
      const existing = grouped.get(record.userId);
      if (existing) {
        existing.records.push(record);
      } else {
        grouped.set(record.userId, {
          userName: record.userName || record.userId,
          records: [record],
        });
      }
    });

    return grouped;
  }
}

// Export singleton instance
export const locationService = new LocationService();
export default locationService;
